import type { ArtifactEffectsWire } from '@kenocyber/shared';

/**
 * Artifact effects, written out in Brazilian Portuguese.
 *
 * The server sends every effect as an exact integer in a fixed unit - basis
 * points, whole Gold, milli-health per second, micro-attacks per second,
 * micro percentage points - and this is the only place they become text.
 *
 * Nothing here goes through `Number`. An artifact is worth 512x its level-1
 * value at level 10, and the whole point of the integer units is that the
 * doubling stays exact; parsing to a float to print it would throw that away
 * at the last step. The division is done on the digits.
 */

/**
 * `value / 10^decimals`, in pt-BR, with trailing zeros trimmed.
 *
 * Exact by construction: the integer part and the fraction are both slices of
 * the decimal string, so 30 720 000 micro-attacks prints as `30,72` and not as
 * `30,719999999999999`.
 */
export function formatScaled(value: string, decimals: number): string {
  const negative = value.startsWith('-');
  const digits = (negative ? value.slice(1) : value).padStart(decimals + 1, '0');
  const whole = digits.slice(0, digits.length - decimals) || '0';
  const fraction = decimals === 0 ? '' : digits.slice(digits.length - decimals).replace(/0+$/, '');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const sign = negative ? '-' : '';
  return fraction.length === 0 ? `${sign}${grouped}` : `${sign}${grouped},${fraction}`;
}

/** Basis points as a percentage: 10 000 bps = 100%. */
export function formatBps(value: string): string {
  return `${formatScaled(value, 2)}%`;
}

/** One line of an artifact's contribution, ready to render. */
export interface ArtifactEffectLine {
  readonly key: keyof ArtifactEffectsWire;
  readonly label: string;
  readonly value: string;
}

/**
 * Every non-zero effect, in a fixed order.
 *
 * Zero-valued effects are skipped rather than printed as `+0%`: an artifact
 * that does not touch attack speed should not claim a row about it. The order
 * is the calculation order documented in `hunt/passive-attributes.ts` - flat
 * bases first, then percentages - so reading the list top to bottom matches
 * how the numbers are actually applied.
 */
export function artifactEffectLines(effects: ArtifactEffectsWire): ArtifactEffectLine[] {
  const lines: ArtifactEffectLine[] = [];
  const push = (key: keyof ArtifactEffectsWire, label: string, value: string) => {
    if (effects[key] !== '0') {
      lines.push({ key, label, value });
    }
  };
  push('baseHealth', 'Vida base', `+${formatScaled(effects.baseHealth, 0)}`);
  push(
    'baseAttackSpeedMicro',
    'Velocidade de ataque base',
    `+${formatScaled(effects.baseAttackSpeedMicro, 6)}/s`,
  );
  push('goldPerKill', 'Ouro por abate', `+${formatScaled(effects.goldPerKill, 0)}`);
  push('damageBps', 'Dano', `+${formatBps(effects.damageBps)}`);
  push('maxHealthBps', 'Vida máxima', `+${formatBps(effects.maxHealthBps)}`);
  push('goldBps', 'Ouro', `+${formatBps(effects.goldBps)}`);
  push(
    'regenMilliPerSecond',
    'Recuperação de vida',
    `+${formatScaled(effects.regenMilliPerSecond, 3)}/s`,
  );
  // MICRO percentage points: 1 000 units is 0,001 p.p., so the divisor is 10^6
  // exactly as it is for attack speed - not 10^3, which would print a thousand
  // times the real chance.
  push(
    'babyAlienChanceMicroPp',
    'Chance de Baby Alien',
    `+${formatScaled(effects.babyAlienChanceMicroPp, 6)} p.p.`,
  );
  return lines;
}

/** True when an artifact contributes nothing at all - it is not owned. */
export function isEmptyEffects(effects: ArtifactEffectsWire): boolean {
  return Object.values(effects).every((value) => value === '0');
}
